import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../hooks/store.hook";
import { useDebounce } from "use-debounce";
import {
  setSearchValueReadySendSheet,
  setSelectedPracticeReadySendSheet,
  setSearchPracticeValueReadySendSheet,
  setPageNumberReadySendSheet,
} from "../../../../../stores/actions/management.actions";
import { DoubleStructure } from "../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  AlignmentEnum,
  FontWeightEnum,
  IconSizeEnum,
  PageNames,
} from "../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import SearchInput from "../../../../../components/core/SearchInput/SearchInput";
import {
  PC_URGES_LABEL,
  PC_URGES_MESSAGE,
  PC_URGES_MESSAGE_TOOLTIP,
  CM_URGES_SENDED_LABEL,
  COLLABORATOR_LABEL,
  COLLABORATORS_LABEL,
  EMPTY_READY_SEND_SHEET_MESSEGE,
  NAME_SURNAME_EMAIL_LABEL,
  NUMBER_CM_URGES_SENDED_LABEL,
  PRACTICE_LABEL,
} from "../../../../../constants/Dictionary";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import {
  Table,
  TableThemeEnum,
} from "../../../../../components/core/Table/Table";
import { getArchivingReadySendSheet } from "../../../../../constants/TableColumns";
import emptyArchivedPendingImage from "../../../../../images/archivedReadyToSend.png";
import {
  StyledFinalFeedbackPendingContainer,
  StyledSearchSelectContainer,
} from "../PendingFinalFeedback/PendingFinalFeedback.styled";
import { StyledArchivedFooter } from "./ReadySendSheet.styled";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { ReactComponent as SendIcon } from "../../../../../icons/send.svg";
import { ReactComponent as InfoIcon } from "../../../../../icons/info.svg";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";
import { theme } from "../../../../../constants/StyledComponents";
import { Link, LinkSizeEnum } from "../../../../../components/core/Link/Link";
import {
  CmSheetsStats,
  ConfigCollaborator,
} from "../../../../../types/api/configurations.type";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { StyledLoadingContainer } from "../../../Management.styled";
import { ReactComponent as AnnouncementIcon } from "../../../../../icons/announcement.svg";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import {
  useReadySendSheetCollaborators,
  useSolicitAllCm,
} from "../../../../../hooks/manageArchiving.hook";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../constants/AppConstants";
import {
  DETAIL_QUERY_PARAM_KEY,
  encodeParams,
} from "../../../../../utils/queryParams.utils";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";

type ReadySendSheetProps = {
  stats: CmSheetsStats;
};

export const ReadySendSheet = ({ stats }: ReadySendSheetProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [hasData, setHasData] = useState<boolean | null>(null);
  const [openPopup, setOpenPopup] = useState(false);
  const { mutateAsync: solicitAllCm, isPending: isSolicitingAllCm } =
    useSolicitAllCm();

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const {
    searchValueReadySendSheet,
    selectedPracticeReadySendSheet,
    searchPracticeValueReadySendSheet,
    pageNumberReadySendSheet,
  } = useAppSelector((state) => state.management);

  const [searchDebouncedValue] = useDebounce(searchValueReadySendSheet, 500);

  const isFirstRender = useRef(true);

  const practiceList = orgSettings.areaList.filtered.map(
    (item) => item.practiceList,
  )[0];

  const { data, isFetching } = useReadySendSheetCollaborators({
    pageNumberReadySendSheet,
    searchDebouncedValue,
    selectedPracticeReadySendSheet,
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPageNumberReadySendSheet(0));
  }, [searchDebouncedValue, selectedPracticeReadySendSheet, dispatch]);

  useEffect(() => {
    if (data && hasData === null) {
      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);
    }
  }, [data, hasData]);

  const handlePopupUrges = async () => {
    try {
      await solicitAllCm();
    } finally {
      setOpenPopup(false);
    }
  };

  const cmUrgesSendedToday = stats.solicitLastUpdateDate
    ? new Date(stats.solicitLastUpdateDate).toDateString() ===
      new Date().toDateString()
    : false;

  const filteredPracticeOptions =
    searchPracticeValueReadySendSheet && practiceList
      ? practiceList.filter((option) =>
          option.name
            .toLowerCase()
            .includes(searchPracticeValueReadySendSheet.toLowerCase()),
        )
      : practiceList || [];

  return (
    <StyledFinalFeedbackPendingContainer $readySendSheet>
      {isFetching && hasData === null ? (
        <StyledLoadingContainer>
          <Loader loading={true} />
        </StyledLoadingContainer>
      ) : null}
      {hasData ? (
        <>
          <DoubleStructure
            verticalAlignment={AlignmentEnum.CENTER}
            variant="primary"
          >
            <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
              {data &&
                `${data.pagination.total} ${data.pagination.total === 1 ? COLLABORATOR_LABEL : COLLABORATORS_LABEL}`}
            </BodyTyp>
            <StyledSearchSelectContainer>
              <SearchInput
                value={searchValueReadySendSheet}
                onChange={(value) =>
                  dispatch(setSearchValueReadySendSheet(value))
                }
                label={NAME_SURNAME_EMAIL_LABEL}
                name={"search"}
              />
              <Select
                variant={SelectVariantEnum.SINGLE_SELECT}
                label={PRACTICE_LABEL}
                options={filteredPracticeOptions.map((items) => ({
                  value: items.id,
                  label: items.name,
                }))}
                onChange={(value) =>
                  dispatch(setSelectedPracticeReadySendSheet(value))
                }
                value={selectedPracticeReadySendSheet}
                withSearch
                onSearch={(value) =>
                  dispatch(setSearchPracticeValueReadySendSheet(value))
                }
                searchValue={searchPracticeValueReadySendSheet}
              />
            </StyledSearchSelectContainer>
          </DoubleStructure>
          <Table<ConfigCollaborator>
            loading={isFetching}
            headers={getArchivingReadySendSheet(orgSettings.areaList.all)}
            scrollY={"calc(100dvh - 370px)"}
            data={data?.data ?? []}
            clickableRow={(record) =>
              !profile.restrictedCollaboratorIds.includes(record.id)
            }
            onRow={(record) => {
              record.id === profile.id
                ? navigate(`${pathname}${PageNames.PERSONAL_PROFILE}`)
                : navigate(
                    `${pathname}/collaborators/${record.id}?${DETAIL_QUERY_PARAM_KEY}=${encodeParams(
                      {
                        collaboratorName: record.firstName,
                        collaboratorSurname: record.lastName,
                      },
                    )}`,
                  );
            }}
            pagination={{
              current: pageNumberReadySendSheet + 1,
              onChange: (pageNumber) =>
                dispatch(setPageNumberReadySendSheet(pageNumber - 1)),
              total: data?.pagination.total ?? 0,
              pageSize: COLLABORATORS_PAGE_SIZE,
            }}
            theme={TableThemeEnum.DARK}
          />
          <StyledArchivedFooter>
            <div>
              {!cmUrgesSendedToday ? (
                <Link
                  onClick={() => setOpenPopup(true)}
                  icon={<AnnouncementIcon />}
                  size={LinkSizeEnum.SMALL}
                >
                  {PC_URGES_LABEL}
                </Link>
              ) : (
                <Link icon={<SendIcon />} size={LinkSizeEnum.SMALL} disabled>
                  {CM_URGES_SENDED_LABEL}
                </Link>
              )}
              <Tooltip message={PC_URGES_MESSAGE} width={250}>
                <IconManager
                  size={IconSizeEnum.MEDIUM}
                  color={theme.colors.purple}
                >
                  <InfoIcon />
                </IconManager>
              </Tooltip>
            </div>
            <div>
              <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                {NUMBER_CM_URGES_SENDED_LABEL}
              </BodyTyp>
              <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                {stats.solicitCount ?? 0}
              </BodyTyp>
            </div>
          </StyledArchivedFooter>
        </>
      ) : null}
      {!isFetching && hasData === false ? (
        <div className="empty-state">
          <EmptyState
            label={EMPTY_READY_SEND_SHEET_MESSEGE}
            imageSrc={emptyArchivedPendingImage}
          />
        </div>
      ) : null}
      {openPopup && (
        <PopupConfirm
          isOpen={openPopup}
          label={PC_URGES_LABEL}
          popupText={PC_URGES_MESSAGE_TOOLTIP}
          onConfirm={() => handlePopupUrges()}
          onCancel={() => setOpenPopup(false)}
          isLoading={isSolicitingAllCm}
        />
      )}
    </StyledFinalFeedbackPendingContainer>
  );
};
